import styled from '@emotion/styled'
import type { TypographyColor } from '@polycups/react'
import cx from 'classnames'

import { BodyM } from '@/legacy/components/Typography'

import { ScreenReaderHidden } from '../ScreenReaderHidden'
import { VisuallyHidden } from '../VisuallyHidden'

export type ItemType = {
  label: string
  ariaLabel?: string
  ariaHidden?: boolean
  href?: string
}[]

export interface BreadcrumbProps {
  items: ItemType
  color?: TypographyColor
  ariaHidden?: boolean
  className?: string
}

const BreadcrumbList = styled.ol`
  word-break: break-word;

  li {
    display: inline;
  }
`

const DividerSymbol = styled(ScreenReaderHidden)`
  margin: 0 0.25rem;

  &.first-element {
    display: none;
  }
`

const Breadcrumb = ({ items, color = 'accent', ariaHidden, className }: BreadcrumbProps) => (
  <div className="eu-sm-1">
    <VisuallyHidden>Navegação do blog</VisuallyHidden>
    <BreadcrumbList>
      {items.map((item, index) => {
        const isLastItem = index === items.length - 1
        const hasMoreThanOneItem = items.length > 1
        const isReferCurrentPage = hasMoreThanOneItem && isLastItem

        const ariaCurrent = isReferCurrentPage ? 'page' : undefined
        const isFirstBreadcrumbItem = index === 0
        const isLinkableItem = !!item.href
        const tag = isLinkableItem ? 'a' : 'span'

        return (
          <li key={`bread-item-${index}`} aria-hidden={item.ariaHidden || ariaHidden}>
            <DividerSymbol
              className={cx({ 'first-element': isFirstBreadcrumbItem })}
              hidden={isFirstBreadcrumbItem}
            >
              {'/'}
            </DividerSymbol>
            <BodyM
              tag={tag}
              href={item.href}
              color={color}
              emphasys={isFirstBreadcrumbItem || isLinkableItem}
              aria-label={item.ariaLabel}
              aria-current={ariaCurrent}
              className={cx(
                { '--dark-high': !isLinkableItem, 'et-text-primary': color === 'accent' },
                className,
              )}
            >
              {item.label}
            </BodyM>
          </li>
        )
      })}
    </BreadcrumbList>
  </div>
)

export default Breadcrumb
